import {
  ShoppingCartOutlined, DollarOutlined, BankOutlined,
  SwapOutlined, RollbackOutlined, TeamOutlined, WarningOutlined,
  ShopOutlined, WalletOutlined, GiftOutlined, AccountBookOutlined,
  RiseOutlined, ArrowDownOutlined, ArrowUpOutlined, StopOutlined,
} from "@ant-design/icons";
import { Tag } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import i18n from "i18next";
import { T } from "../../../shared/ui/PageShell";
import { parseLocalizedName } from "../../../utils/localization";
import { orderDocNo } from "../../../shared/lib/documentNumbers";

export const Chip = ({ children, color }) => (
  <Tag
    color={color || "default"}
    style={{ margin: 0, borderRadius: 7, fontWeight: 600, border: "none" }}
  >
    {children}
  </Tag>
);

/** Card key → detail page config (own route under /reports/accountant/:metric). */
export function buildMetricPages(t) {
  return {
    sales: {
      section: "sales",
      label: t("accTotalSales", "Total Sales"),
      icon: <ShoppingCartOutlined style={{ color: "#0891b2" }} />,
      iconBg: "#cffafe",
      getValue: (m, money) => money(m.total_sales),
    },
    cash: {
      section: "transactions",
      label: t("accCashOnHand", "Cash on Hand"),
      icon: <WalletOutlined style={{ color: "#d97706" }} />,
      iconBg: "#fef3c7",
      getValue: (m, money) => money(m.cash_on_hand ?? 0),
      getColor: (m) => ((m.cash_on_hand ?? 0) >= 0 ? T.green : T.red),
      getSubValue: (m, money, tt) =>
        `${tt("accCashFromSales", "Sales")}: ${money(m.cash_from_sales ?? 0)} · ${tt("accCashFromDebts", "Debt payments")}: ${money(m.cash_from_debt_payments ?? 0)}`,
    },
    taxes: {
      section: "sales",
      label: t("accTaxes", "Taxes"),
      icon: <DollarOutlined style={{ color: "#2563eb" }} />,
      iconBg: "#dbeafe",
      getValue: (m, money) => money(m.taxes),
      getColor: () => "#2563eb",
    },
    wh: {
      section: "company_expenses",
      label: t("accCompanyExpenses", "Warehouse Expenses (مصرفی محل)"),
      icon: <BankOutlined style={{ color: "#ea580c" }} />,
      iconBg: "#ffedd5",
      getValue: (m, money) => money(m.warehouse_expenses ?? m.company_expenses ?? 0),
      getColor: () => "#ea580c",
    },
    cdebt: {
      section: "customer_debts",
      label: t("accCustomerDebts", "Customer Debts"),
      icon: <ArrowDownOutlined style={{ color: "#16a34a" }} />,
      iconBg: "#dcfce7",
      getValue: (m, money) => money(m.customer_debts),
      getColor: () => T.green,
    },
    fout: {
      section: "farmers",
      label: t("accFarmerOutstanding", "Farmer Outstanding Balance"),
      icon: <ArrowUpOutlined style={{ color: "#e11d48" }} />,
      iconBg: "#fee2e2",
      getValue: (m, money) => money(m.farmer_outstanding),
      getColor: () => T.red,
    },
    profit: {
      section: "sales",
      label: t("accNetProfit", "Net Profit"),
      icon: <RiseOutlined style={{ color: "#7c3aed" }} />,
      iconBg: "#ede9fe",
      getValue: (m, money) => money(m.net_profit),
      getColor: (m) => ((m.net_profit || 0) >= 0 ? "#7c3aed" : T.red),
    },
    ret: {
      section: "returns",
      label: t("accReturns", "Returns"),
      icon: <RollbackOutlined style={{ color: "#e11d48" }} />,
      iconBg: "#fee2e2",
      getValue: (m, money) => money(m.returns),
      getColor: () => T.red,
    },
    can: {
      section: "cancellations",
      label: t("accCancellations", "Cancellations"),
      icon: <StopOutlined style={{ color: "#b45309" }} />,
      iconBg: "#fef3c7",
      getValue: (m, money) => money(m.cancellations),
      getColor: () => "#b45309",
    },
    audit: {
      section: "audit",
      label: t("accReversalAudit", "System Audit"),
      icon: <WarningOutlined style={{ color: "#b45309" }} />,
      iconBg: "#fef3c7",
      getValue: (m) => Number(m.order_changes ?? 0).toLocaleString("en-US"),
      getColor: () => "#b45309",
      getSubValue: (m, money, tt) =>
        `${tt("refunded", "Cash refunded")}: ${money(m.cash_refunds ?? 0)} · ${tt("cancelled", "Cancelled sales")}: ${money(m.cancellations ?? 0)}`,
    },
    kg: {
      section: "products",
      label: t("accSoldWeight", "Total Items Sold by Weight"),
      icon: <GiftOutlined style={{ color: "#0d9488" }} />,
      iconBg: "#ccfbf1",
      getValue: (m, _money, tt) =>
        `${Number(m.sold_weight_kg || 0).toLocaleString("en-US")} ${tt("kg", "KG")}`,
    },
    // Farmer Charges card — temporarily hidden
    // fch: {
    //   section: "farmer_charges",
    //   label: t("accFarmerCharges", "Farmer Charges"),
    //   icon: <ShopOutlined style={{ color: "#d97706" }} />,
    //   iconBg: "#fef3c7",
    //   getValue: (m, money) => money(m.farmer_charges),
    //   getColor: () => "#d97706",
    // },
    fex: {
      section: "farmer_charges",
      /** Type code comes only from System Settings — never from overview/type filters. */
      settingsExpenseType: true,
      label: t("accConfiguredFarmerExpense", "Farmer Expense (Settings)"),
      icon: <DollarOutlined style={{ color: "#0f766e" }} />,
      iconBg: "#ccfbf1",
      getValue: (m, money) => money(m.configured_farmer_expense ?? 0),
      getColor: () => "#0f766e",
    },
    cogs: {
      section: "expenses",
      label: t("accCogs", "Cost of Goods Sold"),
      icon: <AccountBookOutlined style={{ color: "#e11d48" }} />,
      iconBg: "#fee2e2",
      getValue: (m, money) => money(m.cogs),
      getColor: () => T.red,
    },
  };
}

function resolveExpenseTypeLabel(v, r, labelMap = {}) {
  const code = String(r?.type_code || "").trim();
  const raw = String(v || r?.type_name || r?.type || "").trim();
  const label = labelMap[code] || labelMap[raw] || r?.type_name || raw || code;
  return label || "—";
}

function auditValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (value.en || value.ar || value.ku || value.tr) {
      return value[i18n.language] || value.en || value.ar || value.ku || value.tr || JSON.stringify(value);
    }
    return JSON.stringify(value);
  }
  if (typeof value === "string" && (value.startsWith("{") || value.startsWith("["))) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
        return parsed[i18n.language] || parsed.en || parsed.ar || parsed.ku || JSON.stringify(parsed);
      }
      return JSON.stringify(parsed);
    } catch {
      // Keep malformed/ordinary strings visible as originally stored.
    }
  }
  return String(value);
}

const AUDIT_NOISE_FIELDS = new Set([
  "id", "order_id", "product_id", "vendor_id", "customer_id", "user_id", "created_by",
  "updated_by", "settlement_archive_id", "deleted_at", "uuid", "token",
]);

function friendlyFieldLabel(key, t) {
  const map = {
    total_price: t("total", "Total"),
    amount_paid: t("amountPaid", "Amount paid"),
    payment_method: t("payment", "Payment"),
    status: t("status", "Status"),
    quantity: t("qty", "Qty"),
    weight: t("weight", "Weight"),
    single_price: t("unitPrice", "Unit price"),
    unit_price: t("unitPrice", "Unit price"),
    name: t("name", "Name"),
    phone: t("phone", "Phone"),
    commission_amount: t("accCommission", "Commission"),
    is_taxed: t("accTaxPayer", "Taxed"),
    cancellation_reason: t("reason", "Reason"),
    note: t("note", "Note"),
  };
  return map[key] || key.replaceAll("_", " ");
}

function AuditChanges({ row, t }) {
  const oldValues = row.old_values || {};
  const newValues = row.new_values || {};
  const keys = Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]))
    .filter((key) => !AUDIT_NOISE_FIELDS.has(key));

  if (!keys.length) {
    return <span style={{ color: T.sub }}>{row.reason || row.activity_detail || "—"}</span>;
  }

  return (
    <div style={{ minWidth: 220, maxWidth: 480, fontSize: 12 }}>
      {keys.slice(0, 8).map((key) => (
        <div key={key} style={{ marginBottom: 3, whiteSpace: "normal", overflowWrap: "anywhere" }}>
          <b style={{ color: T.sub }}>{friendlyFieldLabel(key, t)}:</b>{" "}
          <span style={{ color: T.red }}>{auditValue(oldValues[key])}</span>
          <span style={{ color: T.muted }}> → </span>
          <span style={{ color: T.green }}>{auditValue(newValues[key])}</span>
        </div>
      ))}
      {keys.length > 8 ? (
        <div style={{ color: T.muted, fontSize: 11 }}>+{keys.length - 8} {t("more", "more")}</div>
      ) : null}
    </div>
  );
}

/** Human activity label so Order / OrderProduct noise becomes clear. */
function auditActivity(row, t) {
  const entity = String(row.entity || "").replace(/^App\\Models\\/, "");
  const type = String(row.type || row.action || "");
  const nv = row.new_values || {};
  const productName = auditValue(nv.name || nv.product_name || nv.product || null);
  const hasProduct = productName && productName !== "—";

  if (type === "cancellation") return { label: t("cancelled", "Cancelled"), color: "error", detail: row.reason };
  if (type === "archive") return { label: t("archived", "Archived"), color: "default", detail: null };
  if (type === "refund" || type === "return_refund") return { label: t("accReturnRefund", "Return / refund"), color: "error", detail: row.reason };
  if (type === "credit_reduction") return { label: t("debtReduced", "Debt reduced"), color: "warning", detail: null };
  if (type === "payment_adjustment") return { label: t("accPaymentAdjustment", "Payment adjusted"), color: "processing", detail: null };
  if (type === "order_change") return { label: t("accOrderChange", "Sale edited"), color: "processing", detail: null };
  if (type === "permissions_changed") return { label: t("accPermissionsChanged", "Permissions changed"), color: "warning", detail: null };
  if (type === "image_uploaded") return { label: t("productImageUploaded", "Image uploaded"), color: "blue", detail: null };

  if (entity === "Order" || entity === "order") {
    if (type === "created") return { label: t("accAuditSaleCreated", "Sale created"), color: "success", detail: row.customer || null };
    if (type === "updated") return { label: t("accAuditSaleUpdated", "Sale updated"), color: "processing", detail: null };
    if (type === "deleted" || type === "force_deleted") return { label: t("accAuditSaleDeleted", "Sale deleted"), color: "error", detail: null };
    if (type === "restored") return { label: t("accAuditSaleRestored", "Sale restored"), color: "success", detail: null };
  }

  if (entity === "OrderProduct" || entity === "order_product") {
    if (type === "created") return { label: t("accAuditLineAdded", "Product added to sale"), color: "success", detail: hasProduct ? productName : null };
    if (type === "updated") return { label: t("accAuditLineUpdated", "Sale line changed"), color: "processing", detail: hasProduct ? productName : null };
    if (type === "deleted" || type === "force_deleted") return { label: t("accAuditLineRemoved", "Product removed from sale"), color: "error", detail: hasProduct ? productName : null };
  }

  if (entity === "Product") {
    if (type === "created") return { label: t("accAuditProductCreated", "Product created"), color: "success", detail: hasProduct ? productName : null };
    if (type === "updated") return { label: t("accAuditProductUpdated", "Product updated"), color: "processing", detail: hasProduct ? productName : null };
    if (type === "deleted") return { label: t("accAuditProductDeleted", "Product deleted"), color: "error", detail: hasProduct ? productName : null };
  }

  if (entity === "Customer") {
    if (type === "created") return { label: t("accAuditCustomerCreated", "Customer added"), color: "success", detail: auditValue(nv.name) !== "—" ? auditValue(nv.name) : null };
    if (type === "updated") return { label: t("accAuditCustomerUpdated", "Customer updated"), color: "processing", detail: null };
  }

  if (entity === "Vendor") {
    if (type === "created") return { label: t("accAuditVendorCreated", "Farmer added"), color: "success", detail: auditValue(nv.name) !== "—" ? auditValue(nv.name) : null };
    if (type === "updated") return { label: t("accAuditVendorUpdated", "Farmer updated"), color: "processing", detail: null };
  }

  const fallback = type ? type.replaceAll("_", " ") : (entity || "—");
  return { label: fallback, color: "blue", detail: entity && entity !== "Order" ? entity : null };
}

/** Drop noisy “line created” rows that flood the log when a sale is opened. */
function filterAuditRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((row) => {
    const entity = String(row.entity || "").replace(/^App\\Models\\/, "");
    const type = String(row.type || row.action || "");
    if ((entity === "OrderProduct" || entity === "order_product") && type === "created") {
      return false;
    }
    return true;
  });
}

export function buildSectionDefs(sections, money, t, typeLabels = {}) {
  const companyLabels = typeLabels.company || {};
  const farmerLabels = typeLabels.farmer || {};
  const s = sections;
  return [
    {
      key: "sales",
      icon: <ShoppingCartOutlined style={{ color: "#0891b2" }} />,
      title: t("accSectionSales", "Sales"),
      chips: [
        <Chip key="c">{s.sales?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="s" color="cyan">{t("accTotalSales", "Sales")}: {money(s.sales?.stats?.total_sales ?? 0)}</Chip>,
        <Chip key="p" color="blue">{t("accTotalPaid", "Paid")}: {money(s.sales?.stats?.total_paid)}</Chip>,
        <Chip key="t" color="purple">{t("accCommission", "Commission")}: {money(s.sales?.stats?.tax)}</Chip>,
      ],
      columns: [
        { title: <b>{t("docNo", "No.")}</b>, dataIndex: "document_no", width: 140, render: (v, r) => <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>{v || orderDocNo(r)}</span> },
        { title: <b>{t("date", "Date")}</b>, dataIndex: "date", width: 150, render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span> },
        { title: <b>{t("customer", "Customer")}</b>, dataIndex: "customer", render: (v) => <span style={{ fontWeight: 600 }}>{v || "—"}</span> },
        { title: <b>{t("total", "Total")}</b>, dataIndex: "total", align: "right", render: money },
        { title: <b>{t("paid", "Paid")}</b>, dataIndex: "paid", align: "right", render: money },
        { title: <b>{t("accCommission", "Commission")}</b>, dataIndex: "tax", align: "right", render: (v) => <b style={{ color: "#7c3aed" }}>{money(v)}</b> },
        {
          title: <b>{t("accTaxPayer", "Taxed")}</b>,
          dataIndex: "tax_payer",
          render: (v) => (v === "customer" ? <Tag color="blue">{t("yes", "Yes")}</Tag> : <Tag>{t("no", "No")}</Tag>),
        },
        { title: <b>{t("payment", "Payment")}</b>, dataIndex: "payment" },
      ],
      rows: s.sales?.rows || [],
    },
    {
      key: "expenses",
      icon: <DollarOutlined style={{ color: "#d97706" }} />,
      title: t("accSectionExpenses", "Purchase costs"),
      chips: [
        <Chip key="c">{s.expenses?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="t" color="orange">{t("total", "Total")}: {money(s.expenses?.stats?.total)}</Chip>,
      ],
      columns: [
        { title: <b>ID</b>, dataIndex: "id", width: 72 },
        { title: <b>{t("date", "Date")}</b>, dataIndex: "date", width: 150, render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span> },
        { title: <b>{t("type", "Type")}</b>, dataIndex: "type" },
        { title: <b>{t("description", "Description")}</b>, dataIndex: "description" },
        { title: <b>{t("amount", "Amount")}</b>, dataIndex: "amount", align: "right", render: (v) => <b style={{ color: T.red }}>{money(v)}</b> },
      ],
      rows: s.expenses?.rows || [],
    },
    {
      key: "company_expenses",
      icon: <BankOutlined style={{ color: "#ea580c" }} />,
      title: t("accSectionCompanyExpenses", "Company expenses"),
      chips: [
        <Chip key="c">{s.company_expenses?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="t" color="orange">{t("total", "Total")}: {money(s.company_expenses?.stats?.total)}</Chip>,
      ],
      columns: [
        { title: <b>ID</b>, dataIndex: "id", width: 72 },
        { title: <b>{t("date", "Date")}</b>, dataIndex: "date", width: 150, render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span> },
        {
          title: <b>{t("type", "Type")}</b>,
          key: "expense_type",
          render: (_, r) => <Tag color="orange">{resolveExpenseTypeLabel(r?.type, r, companyLabels)}</Tag>,
        },
        { title: <b>{t("note", "Note")}</b>, dataIndex: "note", render: (v) => v || "—" },
        { title: <b>{t("amount", "Amount")}</b>, dataIndex: "amount", align: "right", render: (v) => <b style={{ color: "#ea580c" }}>{money(v)}</b> },
        { title: <b>{t("user", "User")}</b>, dataIndex: "user", render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span> },
      ],
      rows: s.company_expenses?.rows || [],
      empty: t("accNoCompanyExpenses", "No company expenses in this period"),
    },
    {
      key: "transactions",
      icon: <SwapOutlined style={{ color: "#2563eb" }} />,
      title: t("accSectionTransactions", "Cash movements"),
      chips: [
        <Chip key="c">{s.transactions?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="i" color="success">{t("accIncome", "In")}: {money(s.transactions?.stats?.income)}</Chip>,
        <Chip key="o" color="error">{t("accOutgoing", "Out")}: {money(s.transactions?.stats?.outgoing)}</Chip>,
        <Chip key="n" color="processing">{t("accNet", "Net")}: {money(s.transactions?.stats?.net)}</Chip>,
      ],
      columns: [
        { title: <b>{t("docNo", "No.")}</b>, dataIndex: "document_no", width: 140, render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>{v || "—"}</span> },
        { title: <b>{t("date", "Date")}</b>, dataIndex: "date", width: 150, render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span> },
        { title: <b>{t("type", "Type")}</b>, dataIndex: "type", render: (v) => <Tag>{v || "—"}</Tag> },
        {
          title: <b>{t("amount", "Amount")}</b>,
          dataIndex: "amount",
          align: "right",
          render: (v) => <b style={{ color: v < 0 ? T.red : T.green }}>{money(v)}</b>,
        },
        { title: <b>{t("description", "Description")}</b>, dataIndex: "description", ellipsis: true },
        { title: <b>{t("customer", "Customer")}</b>, dataIndex: "customer", render: (v) => v || "—" },
        { title: <b>{t("vendorsColName", "Farmer")}</b>, dataIndex: "farmer", render: (v) => v || "—" },
        { title: <b>{t("user", "User")}</b>, dataIndex: "user", render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span> },
      ],
      rows: s.transactions?.rows || [],
    },
    {
      key: "returns",
      icon: <RollbackOutlined style={{ color: "#e11d48" }} />,
      title: t("accSectionReturns", "Returns"),
      chips: [
        <Chip key="c">{s.returns?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="t" color="error">{t("total", "Total")}: {money(s.returns?.stats?.total)}</Chip>,
      ],
      columns: [
        {
          title: <b>{t("order", "Order")}</b>,
          dataIndex: "document_no",
          width: 130,
          render: (v, r) => (
            r.order_id ? (
              <Link to={`/orders/${r.order_id}/view`} style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>
                {v || orderDocNo(r)}
              </Link>
            ) : (v || "—")
          ),
        },
        { title: <b>{t("date", "Date")}</b>, dataIndex: "date", width: 140, render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span> },
        {
          title: <b>{t("customer", "Customer")}</b>,
          dataIndex: "customer",
          render: (v, r) => (
            r.customer_id ? (
              <Link to={`/customers/${r.customer_id}`} style={{ fontWeight: 600 }}>{v || "—"}</Link>
            ) : <span style={{ fontWeight: 600 }}>{v || t("walkIn", "Walk-in")}</span>
          ),
        },
        {
          title: <b>{t("product", "Product")}</b>,
          dataIndex: "product",
          ellipsis: true,
          render: (v, r) => parseLocalizedName(v, i18n.language, r.product_id ? `#${r.product_id}` : "—"),
        },
        { title: <b>{t("vendorsColName", "Farmer")}</b>, dataIndex: "vendor", render: (v) => v || "—" },
        {
          title: <b>{t("quantity", "Qty")}</b>,
          dataIndex: "return_qty",
          align: "right",
          width: 72,
          render: (v, r) => {
            const q = Number(v ?? r.quantity) || 0;
            return q > 0 ? q : <span style={{ color: T.muted }}>—</span>;
          },
        },
        {
          title: <b>{t("weight", "Weight")}</b>,
          dataIndex: "return_weight",
          align: "right",
          width: 90,
          render: (v) => {
            const w = Number(v) || 0;
            return w > 0 ? `${w} ${t("kg", "KG")}` : <span style={{ color: T.muted }}>—</span>;
          },
        },
        { title: <b>{t("total", "Total")}</b>, dataIndex: "total", align: "right", render: (v) => <b style={{ color: T.red }}>{money(v)}</b> },
        {
          title: <b>{t("returnedBy", "Returned by")}</b>,
          dataIndex: "returned_by",
          render: (v, r) => <span style={{ color: T.sub, fontSize: 12 }}>{v || r.user || "—"}</span>,
        },
        {
          title: <b>{t("soldBy", "Sold by")}</b>,
          dataIndex: "sold_by",
          render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
        },
      ],
      rows: s.returns?.rows || [],
    },
    {
      key: "cancellations",
      icon: <StopOutlined style={{ color: "#b45309" }} />,
      title: t("accSectionCancellations", "Cancellations"),
      chips: [
        <Chip key="c">{s.cancellations?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="t" color="warning">{t("total", "Total")}: {money(s.cancellations?.stats?.total ?? s.cancellations?.stats?.refunded)}</Chip>,
      ],
      columns: [
        {
          title: <b>{t("order", "Order")}</b>,
          dataIndex: "document_no",
          width: 130,
          render: (v, r) => (
            r.order_id ? (
              <Link to={`/orders/${r.order_id}/view`} style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>
                {v || orderDocNo(r)}
              </Link>
            ) : (v || "—")
          ),
        },
        {
          title: <b>{t("cancelledAt", "Cancelled")}</b>,
          dataIndex: "date",
          width: 140,
          render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
        },
        {
          title: <b>{t("customer", "Customer")}</b>,
          dataIndex: "customer",
          render: (v, r) => (
            r.customer_id ? (
              <Link to={`/customers/${r.customer_id}`} style={{ fontWeight: 600 }}>{v || "—"}</Link>
            ) : <span style={{ fontWeight: 600 }}>{v || t("walkIn", "Walk-in")}</span>
          ),
        },
        {
          title: <b>{t("paymentMethod", "Payment")}</b>,
          dataIndex: "payment_method",
          render: (v) => (v === "credit" ? t("posCredit", "Credit") : t("posCash", "Cash")),
        },
        {
          title: <b>{t("total", "Total")}</b>,
          dataIndex: "total",
          align: "right",
          render: (v) => <b>{money(v)}</b>,
        },
        {
          title: <b>{t("refunded", "Refunded")}</b>,
          dataIndex: "refunded",
          align: "right",
          render: (v) => <b style={{ color: T.red }}>{money(v)}</b>,
        },
        {
          title: <b>{t("reason", "Reason")}</b>,
          dataIndex: "reason",
          ellipsis: true,
          render: (v) => v || "—",
        },
        {
          title: <b>{t("cancelledBy", "Cancelled by")}</b>,
          dataIndex: "cancelled_by",
          render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
        },
        {
          title: <b>{t("soldBy", "Sold by")}</b>,
          dataIndex: "sold_by",
          render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
        },
      ],
      rows: s.cancellations?.rows || [],
    },
    {
      key: "audit",
      icon: <WarningOutlined style={{ color: "#b45309" }} />,
      title: t("accReversalAudit", "System Audit"),
      chips: [
        <Chip key="c">{filterAuditRows(s.audit?.rows).length} {t("records", "records")}</Chip>,
        <Chip key="ch" color="processing">{t("accOrderChanges", "Changes")}: {s.audit?.stats?.order_changes ?? 0}</Chip>,
        <Chip key="x" color="warning">{t("accCancelCount", "Cancels")}: {s.audit?.stats?.cancellations ?? 0}</Chip>,
        <Chip key="ret">{t("accReturns", "Returns")}: {s.audit?.stats?.returns ?? 0}</Chip>,
        <Chip key="r" color="error">{t("refunded", "Cash refunded")}: {money(s.audit?.stats?.cash_refunds ?? 0)}</Chip>,
        <Chip key="a">{t("archived", "Archived")}: {s.audit?.stats?.archived ?? 0}</Chip>,
      ],
      columns: [
        {
          title: <b>{t("when", "When")}</b>,
          dataIndex: "date",
          width: 145,
          render: (v) => <span style={{ color: T.muted, fontSize: 12, whiteSpace: "nowrap" }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
        },
        {
          title: <b>{t("accAuditActivity", "What happened")}</b>,
          key: "activity",
          render: (_, row) => {
            const activity = auditActivity(row, t);
            return (
              <div style={{ minWidth: 160 }}>
                <Tag color={activity.color} style={{ marginInlineEnd: 0, fontWeight: 700 }}>{activity.label}</Tag>
                {activity.detail ? (
                  <div style={{ marginTop: 4, fontSize: 12, color: T.sub, fontWeight: 600 }}>{activity.detail}</div>
                ) : null}
              </div>
            );
          },
        },
        {
          title: <b>{t("order", "Sale")}</b>,
          dataIndex: "order_document_no",
          width: 140,
          render: (v, r) => {
            if (!r.order_id && !v) return <span style={{ color: T.muted }}>—</span>;
            const label = v || orderDocNo({ order_id: r.order_id, document_no: v, created_at: r.date });
            return r.order_id ? (
              <Link to={`/orders/${r.order_id}/view`} style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>
                {label}
              </Link>
            ) : (
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>{label}</span>
            );
          },
        },
        {
          title: <b>{t("amount", "Amount")}</b>,
          dataIndex: "amount",
          align: "right",
          width: 120,
          render: (v, r) => {
            const n = Number(v) || 0;
            if (!n) return <span style={{ color: T.muted }}>—</span>;
            const out = r.direction === "out" || n < 0;
            return (
              <b style={{ color: out ? T.red : T.green, whiteSpace: "nowrap" }}>
                {out && n > 0 ? "−" : ""}{money(Math.abs(n))}
              </b>
            );
          },
        },
        {
          title: <b>{t("accAuditChanges", "Details")}</b>,
          key: "changes",
          render: (_, row) => <AuditChanges row={row} t={t} />,
        },
        {
          title: <b>{t("user", "By")}</b>,
          dataIndex: "user",
          width: 120,
          render: (v) => <span style={{ color: T.sub, fontSize: 12, fontWeight: 600 }}>{v || "—"}</span>,
        },
      ],
      rows: filterAuditRows(s.audit?.rows),
      empty: t("accNoReversals", "No activity in this period"),
    },
    {
      key: "customers",
      icon: <TeamOutlined style={{ color: "#16a34a" }} />,
      title: t("accSectionCustomers", "Customers"),
      chips: [
        <Chip key="c">{s.customers?.stats?.count ?? 0}</Chip>,
        <Chip key="s" color="blue">{t("accTotalSales", "Sales")}: {money(s.customers?.stats?.sales)}</Chip>,
        <Chip key="o">{t("accOutstanding", "Outstanding")}: {money(s.customers?.stats?.outstanding)}</Chip>,
      ],
      columns: [
        { title: <b>ID</b>, dataIndex: "id", width: 72 },
        { title: <b>{t("name", "Name")}</b>, dataIndex: "name", render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: <b>{t("accTotalSales", "Total sales")}</b>, dataIndex: "total_sales", align: "right", render: money },
        { title: <b>{t("accOutstanding", "Outstanding")}</b>, dataIndex: "outstanding", align: "right", render: (v) => <b style={{ color: v > 0 ? T.red : T.green }}>{money(v)}</b> },
      ],
      rows: s.customers?.rows || [],
      empty: t("accNoCustomers", "No customer sales in this period"),
    },
    {
      key: "customer_debts",
      icon: <WarningOutlined style={{ color: "#e11d48" }} />,
      title: t("accSectionCustomerDebts", "Customer debts"),
      chips: [
        <Chip key="c">{s.customer_debts?.stats?.count ?? 0}</Chip>,
        <Chip key="d" color="error">{t("accTotalDebt", "Total debt")}: {money(s.customer_debts?.stats?.total_debt)}</Chip>,
      ],
      columns: [
        { title: <b>ID</b>, dataIndex: "id", width: 72 },
        { title: <b>{t("name", "Name")}</b>, dataIndex: "name", render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: <b>{t("phone", "Phone")}</b>, dataIndex: "phone", render: (v) => v || "—" },
        { title: <b>{t("accTotalSales", "Total sales")}</b>, dataIndex: "total_sales", align: "right", render: money },
        { title: <b>{t("accOutstandingDebt", "Outstanding")}</b>, dataIndex: "outstanding", align: "right", render: (v) => <b style={{ color: T.red }}>{money(v)}</b> },
      ],
      rows: s.customer_debts?.rows || [],
      empty: t("accNoCustomerDebts", "No open customer debts"),
    },
    {
      key: "farmers",
      icon: <ShopOutlined style={{ color: "#db2777" }} />,
      title: t("accSectionFarmers", "Farmers"),
      chips: [
        <Chip key="c">{s.farmers?.stats?.count ?? 0}</Chip>,
        <Chip key="v" color="blue">{t("accValue", "Value")}: {money(s.farmers?.stats?.value)}</Chip>,
        <Chip key="o" color="error">{t("accOwed", "Owed")}: {money(s.farmers?.stats?.owed)}</Chip>,
      ],
      columns: [
        { title: <b>ID</b>, dataIndex: "id", width: 72 },
        { title: <b>{t("name", "Name")}</b>, dataIndex: "name", render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: <b>{t("items", "Items")}</b>, dataIndex: "items" },
        { title: <b>{t("quantity", "Qty / kg")}</b>, dataIndex: "quantity" },
        { title: <b>{t("accTotalValue", "Total value")}</b>, dataIndex: "total_value", align: "right", render: money },
        { title: <b>{t("accCharges", "Charges")}</b>, dataIndex: "charges", align: "right", render: (v) => <span style={{ color: T.red }}>{money(v)}</span> },
        { title: <b>{t("accCommission", "Commission")}</b>, dataIndex: "tax_ded", align: "right", render: (v) => (v == null ? "—" : <span style={{ color: "#7c3aed" }}>{money(v)}</span>) },
        { title: <b>{t("accPaidOut", "Paid out")}</b>, dataIndex: "paid_out", align: "right", render: money },
        { title: <b>{t("accNetOwed", "Still owed")}</b>, dataIndex: "net_owed", align: "right", render: (v) => <b style={{ color: v > 0 ? T.red : T.green }}>{money(v)}</b> },
      ],
      rows: s.farmers?.rows || [],
    },
    {
      key: "farmer_charges",
      icon: <WalletOutlined style={{ color: "#d97706" }} />,
      title: t("accSectionFarmerCharges", "Farmer charges"),
      chips: [
        <Chip key="c">{s.farmer_charges?.stats?.count ?? 0} {t("records", "records")}</Chip>,
        <Chip key="t" color="orange">{t("total", "Total")}: {money(s.farmer_charges?.stats?.total)}</Chip>,
      ],
      columns: [
        { title: <b>ID</b>, dataIndex: "id", width: 72 },
        { title: <b>{t("date", "Date")}</b>, dataIndex: "date", render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span> },
        {
          title: <b>{t("type", "Type")}</b>,
          key: "expense_type",
          render: (_, r) => <Tag color="orange">{resolveExpenseTypeLabel(r?.type, r, farmerLabels)}</Tag>,
        },
        { title: <b>{t("vendorsColName", "Farmer")}</b>, dataIndex: "farmer", render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: <b>{t("amount", "Amount")}</b>, dataIndex: "amount", align: "right", render: (v) => <b style={{ color: "#d97706" }}>{money(v)}</b> },
        { title: <b>{t("user", "User")}</b>, dataIndex: "user" },
      ],
      rows: s.farmer_charges?.rows || [],
    },
    {
      key: "products",
      icon: <GiftOutlined style={{ color: "#2563eb" }} />,
      title: t("accSectionProducts", "Products by sales"),
      chips: [
        <Chip key="c">{s.products?.stats?.count ?? 0}</Chip>,
        <Chip key="r" color="blue">{t("accRevenue", "Revenue")}: {money(s.products?.stats?.revenue)}</Chip>,
      ],
        columns: [
          { title: <b>{t("product", "Product")}</b>, dataIndex: "product", render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
          { title: <b>{t("accTotalSold", "Sold")}</b>, dataIndex: "total_sold" },
          { title: <b>{t("accTotalRevenue", "Revenue")}</b>, dataIndex: "total_revenue", align: "right", render: money },
          { title: <b>{t("accAvgPrice", "Avg price")}</b>, dataIndex: "avg_price", align: "right", render: money },
        ],
      rows: s.products?.rows || [],
    },
  ];
}
